import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { bankAccountsApi } from '../../api/bankAccounts';
import { paymentExportApi } from '../../api/paymentExport';
import { formatMoney } from '../../utils/formatMoney';
import { formatDate } from '../../utils/formatDate';
import LoadingSpinner from '../../components/LoadingSpinner';

/**
 * Mokėjimų paruošimas bankui (pain.001) — the last step of the banking cycle.
 * Select OUR bank account, check the unpaid supplier invoices (outstanding
 * amounts per the reconciliation read model), export the payment file.
 * READ-ONLY: nothing is posted — the XML goes to the bank, the accountant's
 * books stay untouched until the bank statement comes back through import.
 */
export default function PaymentPreparation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [bankAccountId, setBankAccountId] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const { data: bankAccounts } = useQuery({
    queryKey: ['bank-accounts-payments'],
    queryFn: () => bankAccountsApi.list(),
  });
  // Only EUR accounts with an IBAN can originate pain.001 payments.
  const eligibleAccounts = (bankAccounts ?? []).filter(
    b => b.iban && b.currencyCode === 'EUR' && !b.isObsolete);

  const { data: unpaid, isLoading } = useQuery({
    queryKey: ['unpaid-invoices'],
    queryFn: () => paymentExportApi.unpaidInvoices(),
  });

  const payable = (unpaid ?? []).filter(u => !!u.supplierBankAccount);
  const missingAccount = (unpaid ?? []).filter(u => !u.supplierBankAccount);
  const selectedRows = payable.filter(u => selected[u.invoiceJournalId]);
  const total = useMemo(
    () => selectedRows.reduce((s, u) => s + u.outstandingAmount, 0),
    [selectedRows]);

  const allSelected = payable.length > 0 && payable.every(u => selected[u.invoiceJournalId]);

  const toggleAll = () => {
    setSelected(allSelected ? {} : Object.fromEntries(payable.map(u => [u.invoiceJournalId, true])));
  };

  const exportPayments = async () => {
    if (!bankAccountId || selectedRows.length === 0) return;
    setExporting(true);
    setError(null);
    setDone(null);
    try {
      await paymentExportApi.generate(
        bankAccountId,
        selectedRows.map(u => ({ invoiceJournalId: u.invoiceJournalId, amount: u.outstandingAmount })));
      setDone(t('banking.payments.done', { count: selectedRows.length, total: formatMoney(total) }));
      setSelected({});
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{t('banking.payments.title')}</h1>
          <p className="text-sm text-gray-500">{t('banking.payments.subtitle')}</p>
        </div>
        <button
          className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          onClick={() => navigate('/banking')}
        >
          {t('banking.backToBanking')}
        </button>
      </div>

      {/* Our bank account */}
      <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
        <label className="block text-xs text-gray-500 mb-1">{t('banking.payments.account')}</label>
        {eligibleAccounts.length === 0 ? (
          <p className="text-sm text-amber-700">{t('banking.payments.noAccounts')}</p>
        ) : (
          <select
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md min-w-72"
            value={bankAccountId ?? ''}
            onChange={e => setBankAccountId(Number(e.target.value) || null)}
          >
            <option value="">{t('banking.payments.chooseAccount')}</option>
            {eligibleAccounts.map(b => (
              <option key={b.id} value={b.id}>
                {b.name} · {b.iban}
              </option>
            ))}
          </select>
        )}
      </div>

      {done && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2.5 text-sm text-green-800">
          ✓ {done}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 text-sm text-red-700">{error}</div>
      )}

      {missingAccount.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-800">
          {t('banking.payments.missingAccount', { count: missingAccount.length })}
          <ul className="mt-1 text-xs list-disc list-inside">
            {missingAccount.slice(0, 5).map(u => (
              <li key={u.invoiceJournalId}>{u.supplierName} — {u.documentNumber}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Unpaid invoices */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
              <th className="px-3 py-2 w-10">
                <input
                  type="checkbox"
                  className="accent-primary-600"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label={t('banking.payments.selectAll')}
                />
              </th>
              <th className="px-3 py-2 font-semibold">{t('banking.payments.col.doc')}</th>
              <th className="px-3 py-2 font-semibold">{t('banking.payments.col.date')}</th>
              <th className="px-3 py-2 font-semibold">{t('banking.payments.col.supplier')}</th>
              <th className="px-3 py-2 font-semibold text-right">{t('banking.payments.col.amount')}</th>
            </tr>
          </thead>
          <tbody>
            {payable.map(u => (
              <tr key={u.invoiceJournalId} className="border-t border-gray-50 hover:bg-gray-50">
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    className="accent-primary-600"
                    checked={!!selected[u.invoiceJournalId]}
                    onChange={e => setSelected(prev => ({ ...prev, [u.invoiceJournalId]: e.target.checked }))}
                    aria-label={`${t('banking.payments.select')}: ${u.documentNumber}`}
                  />
                </td>
                <td className="px-3 py-2 font-medium text-gray-900">{u.documentNumber}</td>
                <td className="px-3 py-2 text-gray-500">{formatDate(u.date)}</td>
                <td className="px-3 py-2">
                  <div className="text-gray-800">{u.supplierName}</div>
                  <div className="text-xs text-gray-400 font-mono">{u.supplierBankAccount}</div>
                </td>
                <td className="px-3 py-2 text-right num text-gray-900">{formatMoney(u.outstandingAmount)}</td>
              </tr>
            ))}
            {payable.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">
                  {t('banking.payments.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Total + export */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {t('banking.payments.selected', { count: selectedRows.length })} ·{' '}
          <span className="font-semibold num">{formatMoney(total)}</span>
        </p>
        <button
          onClick={exportPayments}
          disabled={exporting || !bankAccountId || selectedRows.length === 0}
          className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-40"
        >
          {exporting ? '…' : t('banking.payments.export', { count: selectedRows.length })}
        </button>
      </div>
    </div>
  );
}
